import { getOrdersOptions } from "@/api/marketplace/getOrders";
import { Seaport } from "@opensea/seaport-js";
import type {
	CreateOrderInput,
	OrderComponents,
	OrderWithCounter,
} from "@opensea/seaport-js/lib/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Signer } from "ethers";
import { toast } from "sonner";
import { useEthersSigner } from "./useEthersSigner";

export const useSeaport = () => {
	/* Seaport only supports ether. */
	const signer = useEthersSigner();
	const queryClient = useQueryClient();

	const createSellOrder = useMutation({
		mutationFn: async (createOrder: CreateOrderInput) => {
			if (!signer) {
				throw new Error("Signer not found");
			}
			if (
				createOrder.offer.length === 0 ||
				createOrder.consideration.length === 0
			) {
				throw new Error(
					"Offer items and consideration items must not be empty",
				);
			}
			const seaport = new Seaport(signer as unknown as Signer);

			const { executeAllActions } = await seaport.createOrder(createOrder);

			const order = await executeAllActions();
			return order;
		},
		onSuccess: async () => {
			toast.success("Order created");
			await queryClient.invalidateQueries(getOrdersOptions({}));
		},
		onError: (err) => {
			console.error(err);
			toast.error(`Sell Failed: ${err.message.slice(0, 300)}`);
		},
	});

	const cancelOrders = useMutation({
		mutationFn: async (order: OrderComponents[]) => {
			if (!signer) {
				throw new Error("Signer not found");
			}
			const seaport = new Seaport(signer as unknown as Signer);
			const transaction = await seaport.cancelOrders(order).transact();

			return { hash: transaction.hash };
		},
		onSuccess: async () => {
			toast.success("Order cancelled");
			await queryClient.invalidateQueries(getOrdersOptions({}));
		},
		onError: (err) => {
			console.error(err);
			toast.error(`Cancel Failed: ${err.message.slice(0, 300)}`);
		},
	});

	const fulfillOrder = useMutation({
		mutationFn: async (order: OrderWithCounter) => {
			if (!signer) {
				throw new Error("Signer not found");
			}
			const seaport = new Seaport(signer as unknown as Signer);
			const { executeAllActions } = await seaport.fulfillOrder({ order });
			const transaction = await executeAllActions();

			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			return { hash: (transaction as any).hash };
		},
		onSuccess: async () => {
			toast.success("Order fulfilled");
			// Wait for event webhook to be processed
			await new Promise((resolve) => setTimeout(resolve, 1500));
			await queryClient.invalidateQueries(getOrdersOptions({}));
		},
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		onError: async (error: any, order) => {
			console.error("Detailed fulfillment error:", {
				error,
				orderParameters: order.parameters,
			});
			toast.error(`Buy Failed: ${error.message.slice(0, 300)}`);
		},
	});

	return {
		createSellOrder,
		fulfillOrder,
		cancelOrders,
	};
};
